import Table from "@common/Table";
import { cellRenderer, headerCellRenderer, smartRound } from "@utils/helpers";
import { Empty, message, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";

const MAPPED_KEYS = {
  Exporter: "Manufacturer",
  Importer: "Consignee",
  ArrivalCountry: "Country (Consignee)",
  LeavesCountry: "Country (Manufacturer)",
  qtyInKg: "Quantity",
  totalValueUsdHelper: "Price",
  usdPerKg: "Unit Price",
  date: "Date",
};

export default function TableArea({
  data,
  filterColumns,
  setSecondaryFilters,
  secondaryFilters,
  handleSearch,
  hiddenFilters = [],
  loading = false,
}) {
  const [pagination, setPagination] = useState({});
  const [sorter, setSorter] = useState({});
  const prevSorterRef = useRef({});

  // Handle page change
  const handleTablePaginationChange = (newPagination, filter, sorter) => {
    const sortKeyMap = {
      Manufacturer: "manufacturerIds",
      Product: "productIds",
      Consignee: "consigneeIds",
      "Country (Consignee)": "countryConsigneeIds",
      "Country (Manufacturer)": "countryManufacturerIds",
      "Unit Price": "unitPrice",
      Quantity: "quantity",
      Price: "price",
      Date: "date",
      Type: "Type",
    };
    
    const sortKey = sorter?.field ? sortKeyMap[sorter.field] : null;
    const sortOrder =
      sorter?.order === "ascend"
        ? "asc"
        : sorter?.order === "descend"
          ? "desc"
          : null;

    // Check if sort actually changed
    const sortChanged =
      prevSorterRef.current?.field !== sorter?.field ||
      prevSorterRef.current?.order !== sorter?.order;

    // If sort changed, reset to page 1; otherwise use the new pagination page
    const pageToLoad = sortChanged ? 1 : newPagination.current;

    // Update state
    setSorter(sorter);
    setPagination({
      ...pagination,
      current: pageToLoad,
      pageSize: newPagination.pageSize,
    });

    // Update previous sorter reference
    prevSorterRef.current = sorter || {};

    // Only make API call if sort changed or pagination changed
    handleSearch(pageToLoad, sortKey, sortOrder, newPagination.pageSize);
  };

  const handleSetSecondaryFilters = (slug, value) => {
    if (slug === "Product") {
      const restrictedAPIs = data?.secondaryFields?.products
        ?.filter((p) => p.isGramProduct)
        .map((p) => p.id);

      const restrictedCount = value.filter((p) =>
        restrictedAPIs.includes(p),
      ).length;

      const hasOtherAPIs = value.some((p) => !restrictedAPIs.includes(p));

      if (restrictedCount > 0 && hasOtherAPIs) {
        message.error(
          "Latanoprost and Bimatoprost cannot be selected with other APIs",
        );
        return;
      }
    }

    setSecondaryFilters({
      ...secondaryFilters,
      [slug]: value,
    });
  };

  const columns = [
    {
      title: headerCellRenderer("Date", "!flex justify-center"),
      dataIndex: "Date",
      key: "Date",
      render: (text) => cellRenderer(text),
      sorter: true,
      sortOrder: sorter.field === "Date" ? sorter.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: headerCellRenderer(
        <div
          className={`flex flex-col justify-between ${
            filterColumns[0]?.name !== "Manufacturer"
              ? "h-full min-h-[60px]"
              : ""
          }`}
        >
          <label className="text-center">Manufacturer</label>
          {filterColumns[0]?.name === "Manufacturer" ? null : (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Select
                mode="multiple"
                allowClear
                size="small"
                className="!mt-1 !text-xs !w-full"
                placeholder={`All`}
                value={secondaryFilters["Manufacturer"]}
                onChange={(value) =>
                  handleSetSecondaryFilters("Manufacturer", value)
                }
                options={data?.secondaryFields?.manufacturers?.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                popupMatchSelectWidth={false}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </div>
          )}
        </div>,
      ),
      dataIndex: "Manufacturer",
      key: "Manufacturer",
      render: (text) => cellRenderer(text),
      hidden:
        !filterColumns.some((item) => item.name === "Manufacturer") ||
        hiddenFilters.some((item) => item.name === "Manufacturer"),
      sorter: true,
      sortOrder: sorter.field === "Manufacturer" ? sorter.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: headerCellRenderer(
        <div
          className={`flex flex-col justify-between ${
            filterColumns[0]?.name !== "Product" ? "h-full min-h-[60px]" : ""
          }`}
        >
          <label className="text-center">Product</label>
          {filterColumns[0]?.name === "Product" ? null : (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Select
                mode="multiple"
                allowClear
                size="small"
                className="!mt-1 !text-xs !w-full"
                placeholder={`All`}
                value={secondaryFilters["Product"]}
                onChange={(value) =>
                  handleSetSecondaryFilters("Product", value)
                }
                options={data?.secondaryFields?.products?.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                popupMatchSelectWidth={false}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </div>
          )}
        </div>,
      ),
      dataIndex: "Product",
      key: "Product",
      render: (text) => cellRenderer(text),
      hidden:
        !filterColumns.some((item) => item.name === "Product") ||
        hiddenFilters.some((item) => item.name === "Product"),
      sorter: true,
      sortOrder: sorter.field === "Product" ? sorter.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: headerCellRenderer(
        <div
          className={`flex flex-col justify-between ${
            filterColumns[0]?.name !== "Consignee" ? "h-full min-h-[60px]" : ""
          }`}
        >
          <label className="text-center">Consignee</label>
          {filterColumns[0]?.name === "Consignee" ? null : (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Select
                mode="multiple"
                allowClear
                size="small"
                className="!mt-1 !text-xs !w-full"
                placeholder={`All`}
                value={secondaryFilters["Consignee"]}
                onChange={(value) =>
                  handleSetSecondaryFilters("Consignee", value)
                }
                options={data?.secondaryFields?.consignees?.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                popupMatchSelectWidth={false}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </div>
          )}
        </div>,
      ),
      dataIndex: "Consignee",
      key: "Consignee",
      render: (text) => cellRenderer(text),
      hidden:
        !filterColumns.some((item) => item.name === "Consignee") ||
        hiddenFilters.some((item) => item.name === "Consignee"),
      sorter: true,
      sortOrder: sorter.field === "Consignee" ? sorter.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: headerCellRenderer(
        <div
          className={`flex flex-col justify-between ${
            filterColumns[0]?.name !== "Country (Consignee)"
              ? "h-full min-h-[60px]"
              : ""
          }`}
        >
          <label className="text-center">Country (Consignee)</label>
          {filterColumns[0]?.name === "Country (Consignee)" ? null : (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Select
                mode="multiple"
                allowClear
                size="small"
                className="!mt-1 !text-xs !w-full"
                placeholder={`All`}
                value={secondaryFilters["Country (Consignee)"]}
                onChange={(value) =>
                  handleSetSecondaryFilters("Country (Consignee)", value)
                }
                options={data?.secondaryFields?.["country(consignee)"]?.map(
                  (item) => ({
                    label: item.name,
                    value: item.id,
                  }),
                )}
                popupMatchSelectWidth={false}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </div>
          )}
        </div>,
      ),
      dataIndex: "Country (Consignee)",
      key: "Country (Consignee)",
      render: (text) => cellRenderer(text),
      hidden:
        !filterColumns.some((item) => item.name === "Country (Consignee)") ||
        hiddenFilters.some((item) => item.name === "Country (Consignee)"),
      sorter: true,
      sortOrder:
        sorter.field === "Country (Consignee)" ? sorter.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: headerCellRenderer(
        <div
          className={`flex flex-col justify-between ${
            filterColumns[0]?.name !== "Country (Manufacturer)"
              ? "h-full min-h-[60px]"
              : ""
          }`}
        >
          <label className="text-center">Country (Manufacturer)</label>
          {filterColumns[0]?.name === "Country (Manufacturer)" ? null : (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Select
                mode="multiple"
                allowClear
                size="small"
                className="!mt-1 !text-xs !w-full"
                placeholder={`All`}
                value={secondaryFilters["Country (Manufacturer)"]}
                onChange={(value) =>
                  handleSetSecondaryFilters("Country (Manufacturer)", value)
                }
                options={data?.secondaryFields?.["country(manufacturer)"]?.map(
                  (item) => ({
                    label: item.name,
                    value: item.id,
                  }),
                )}
                popupMatchSelectWidth={false}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </div>
          )}
        </div>,
      ),
      dataIndex: "Country (Manufacturer)",
      key: "Country (Manufacturer)",
      render: (text) => cellRenderer(text),
      hidden:
        !filterColumns.some((item) => item.name === "Country (Manufacturer)") ||
        hiddenFilters.some((item) => item.name === "Country (Manufacturer)"),
      sorter: true,
      sortOrder:
        sorter.field === "Country (Manufacturer)" ? sorter.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: headerCellRenderer("Quantity", "!flex justify-center"),
      dataIndex: "Quantity",
      key: "Quantity",
      render: (text) => cellRenderer(text),
      sorter: true,
      sortOrder: sorter.field === "Quantity" ? sorter.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: headerCellRenderer("Price", "!flex justify-center"),
      dataIndex: "Price",
      key: "Price",
      render: (text) => cellRenderer(text),
      sorter: true,
      sortOrder: sorter.field === "Price" ? sorter.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: headerCellRenderer("Unit Price", "!flex justify-center"),
      dataIndex: "Unit Price",
      key: "Unit Price",
      render: (text) => cellRenderer(text),
      sorter: true,
      sortOrder: sorter.field === "Unit Price" ? sorter.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: headerCellRenderer("Type", "!flex justify-center"),
      dataIndex: "Type",
      key: "Type",
      render: (text) => cellRenderer(text),
      sorter: true,
      sortOrder: sorter.field === "Type" ? sorter.order : undefined,
      showSorterTooltip: false,
    },
  ];

  const orderMap = {
    ...Object.fromEntries(
      filterColumns.map((item, index) => [item.name, index + 1]),
    ),
  };

  const sortedColumns = [
    ...columns.slice(0, 1),
    ...columns.slice(1, 6).sort((a, b) => {
      const aOrder = orderMap[a.dataIndex] ?? 999;
      const bOrder = orderMap[b.dataIndex] ?? 999;
      return aOrder - bOrder;
    }),
    ...columns.slice(6),
  ];

  const mappedData = data?.data?.map((item) => {
    const newItem = { ...item };

    // Loop over keys you want to rename
    Object.entries(MAPPED_KEYS).forEach(([oldKey, newKey]) => {
      if (newItem[oldKey] !== undefined) {
        newItem[newKey] = newItem[oldKey]; // assign
        delete newItem[oldKey]; // remove old key
      }
    });

    return newItem;
  });

  const dataSource = mappedData?.map((item) => {
    return {
      Manufacturer: item.Manufacturer.CompanyMaster?.name,
      Product: item.Product.name,
      Consignee: item.Consignee.CompanyMaster?.name,
      ["Country (Consignee)"]: item["Country (Consignee)"].name,
      ["Country (Manufacturer)"]: item["Country (Manufacturer)"].name,
      Quantity:
        item.Product.isGramProduct && item.qtyInG
          ? Number(item.qtyInG).toFixed(2)
          : item.Quantity
            ? Number(item.Quantity).toFixed(2)
            : null,
      Price: item.Price ? smartRound(item.Price) : null,
      ["Unit Price"]:
        item.Product.isGramProduct && item.usdPerG
          ? smartRound(item.usdPerG)
          : item["Unit Price"]
            ? smartRound(item["Unit Price"])
            : null,

      Date: dayjs(item.Date).format("DD MMM YYYY"),
      Type: item.SubTypeMaster.alias,
    };
  });

  useEffect(() => {
    if (data) {
      setPagination({
        current: data.pagination.current_page,
        pageSize: data.pagination.total_perpage,
        total: data.pagination.total_records,
      });
    }
  }, [data]);

  return (
    <div
      className="flex flex-col gap-2.5 w-full md:w-[70%] lg:w-[80%] overflow-x-scroll overflow-y-auto animate-fadeIn custom-scrollbar [&_.ant-table-column-sorter-up]:!text-white [&_.ant-table-column-sorter-down]:!text-white  [&_.ant-table-column-sorter-up.active]:!text-gray-500
  [&_.ant-table-column-sorter-down.active]:!text-gray-500 "
    >
      <Table
        columns={sortedColumns}
        dataSource={dataSource}
        scrollConfig={{ x: 1100 }}
        pagination={pagination}
        onChange={handleTablePaginationChange}
        emptyText="No data to show. Adjust search criteria"
        loading={loading}
      />
    </div>
  );
}
